import { useEffect, useImperativeHandle, useRef, useState, forwardRef, useCallback, useMemo } from "react";
import { IAnalysedProperty, ILoan } from "../../../../interface/addAnalyser";
import { useAnalysedPropertyContext } from "./AnalysedPropertyContext";
import REITab from "../../../../components/ui/tab";
import { REIButton } from "../../../../components/ui";
import { formatDayCycle, getAdjustedFormattedDate, getDateStringWithGivenFormat } from "../../../../utils/commonUtil";
import LoanDetailsTab, { LoanValidation } from "./LoanDetailsTab";

interface ILoanDetailsProps {
  setIsValid: (isValid: boolean) => void;
  updateProperty: (updated: Partial<IAnalysedProperty>) => void;
  loanAmount?: number;
}

const LoanDetails = forwardRef<{ validate: () => boolean }, ILoanDetailsProps>(
  ({ setIsValid, updateProperty, loanAmount }, ref) => {
    const { analysedProperty, userProfile } = useAnalysedPropertyContext();
    const [loans, setLoans] = useState<ILoan[]>([]);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [reset, setReset] = useState<boolean>(false);
    const tabRefs = useRef<(LoanValidation | null)[]>([]);
    const hasInitializedUseDefault = useRef(false);
    const previousUseDefault = useRef<string | null | undefined>(undefined);
    const [loanValidation, setLoanValidation] = useState<Record<string, boolean>>({});

    const createDefaultLoan = useCallback(
      (index: number): ILoan => ({
        LoanId: Date.now() + index,
        Amount: 0,
        Date: getDateStringWithGivenFormat(new Date(Date.now())),
        EstablishmentFees: 500,
        FixedInterestPeriod_Months: 0,
        FixedInterestPeriod_Years: 0,
        InterestRate: 5,
        InterestRateReviewDate: getAdjustedFormattedDate(new Date(Date.now()), { years: 3 }),
        Length_Months: 0,
        Length_Years: 31,
        Name: `${formatDayCycle(index + 1)} loan`,
        OtherLoanCosts: 0,
        ValuationFees: 500,
        IsPrincipal: 0,
        isFromPortfolio: false,
        Balance: 0,
        PropertyAnalyserid: 0,
        isNameModified: false,
      }),
      []
    );

    const applyUserDefaultLoanValues = useCallback(
      (loan: ILoan): ILoan => {
        if (
          analysedProperty?.PropertyDetail.PropertyDetailId !== 0 ||
          analysedProperty?.PropertyDetail.useDefault !== "1" ||
          !userProfile ||
          loan.isFromPortfolio
        ) {
          return loan;
        }

        return {
          ...loan,
          EstablishmentFees: userProfile.LoanCost.EstPrice ?? 0,
          InterestRate: userProfile.MortgageType.InterestRate ?? 5,
          Length_Months: 1,
          Length_Years: userProfile.MortgageType.LengthOfLoan ?? 0,
          OtherLoanCosts: userProfile.LoanCost.OtherLoanCost ?? 0,
          ValuationFees: userProfile.LoanCost.ValuationFee ?? 0,
          IsPrincipal: userProfile.MortgageType.PrincipalNInterest === "1" ? 1 : 0,
        };
      },
      [analysedProperty?.PropertyDetail.PropertyDetailId, analysedProperty?.PropertyDetail.useDefault, userProfile]
    );

    const updateLoanNames = useCallback((loansList: ILoan[]): ILoan[] => {
      return loansList.map((loan, index) => {
        if (!loan.isNameModified) {
          return {
            ...loan,
            Name: `${formatDayCycle(index + 1)} loan`,
          };
        }
        return loan;
      });
    }, []);

    useEffect(() => {
      if (loanAmount && activeIndex === 0 && loans.length === 1) {
        setLoans((prevLoans) =>
          prevLoans.map((loan) => ({
            ...loan,
            Amount: loanAmount,
          }))
        );
      }
    }, [loanAmount]);

    useEffect(() => {
      if (analysedProperty && !reset) {
        setTimeout(() => {
          if (
            analysedProperty &&
            analysedProperty.LoanTypeCost &&
            Array.isArray(analysedProperty.LoanTypeCost.Loans) &&
            analysedProperty.LoanTypeCost.Loans.length > 0
          ) {
            const newLoans = analysedProperty.LoanTypeCost.Loans.map((loan: ILoan) => {
              const nameIsModified = loan.Name.trim() !== "";

              return {
                ...loan,
                isFromPortfolio: false,
                isNameModified: nameIsModified,
              };
            });

            const updatedLoans = updateLoanNames(newLoans);

            if (JSON.stringify(updatedLoans) !== JSON.stringify(loans)) {
              setLoans(updatedLoans);
            }
          } else if (
            analysedProperty &&
            analysedProperty.LoanTypeCost &&
            (!analysedProperty.LoanTypeCost.Loans || analysedProperty.LoanTypeCost.Loans.length === 0)
          ) {
            setLoans([applyUserDefaultLoanValues(createDefaultLoan(0))]);
          }
        }, 200);
      }
    }, [analysedProperty?.LoanTypeCost.Loans, applyUserDefaultLoanValues, createDefaultLoan, reset]);

    useEffect(() => {
      const currentUseDefault = analysedProperty?.PropertyDetail.useDefault;

      if (!hasInitializedUseDefault.current) {
        if (currentUseDefault === undefined) {
          return;
        }

        previousUseDefault.current = currentUseDefault;
        hasInitializedUseDefault.current = true;
        return;
      }

      const didEnableDefaults = previousUseDefault.current !== "1" && currentUseDefault === "1";
      previousUseDefault.current = currentUseDefault;

      if (
        analysedProperty?.PropertyDetail.PropertyDetailId !== 0 ||
        currentUseDefault !== "1" ||
        !userProfile ||
        loans.length === 0 ||
        !didEnableDefaults
      ) {
        return;
      }

      setLoans((prevLoans) => prevLoans.map((loan) => applyUserDefaultLoanValues(loan)));
    }, [
      applyUserDefaultLoanValues,
      analysedProperty?.PropertyDetail.PropertyDetailId,
      analysedProperty?.PropertyDetail.useDefault,
      loans.length,
      userProfile,
    ]);

    useEffect(() => {
      if (loans.length > 0) {
        setIsValid(true);
        setReset(true);
        updateProperty({
          LoanTypeCost: {
            ...analysedProperty?.LoanTypeCost,
            Loans: loans,
            PropertyOtherPurchaseExpense: analysedProperty?.LoanTypeCost?.PropertyOtherPurchaseExpense || {
              CapitalInvestment: 0,
              OtherCost: 0,
              SolicitorsCost: 0,
              StampDuty: 0,
            },
            PropertyPurchaseCost: analysedProperty?.LoanTypeCost?.PropertyPurchaseCost || {
              CashDeposit: 0,
              MarketValueNow: 0,
              PurchaseAmount: 0,
              EquityDeposit: 0,
              isEnterPercentage: "0",
              isEnterValue: "0",
            },
          },
        });
      }
    }, [loans]);

    const validateLoan = (loan: ILoan): boolean => {
      return loan.Amount > 0 && loan.InterestRate > 0 && loan.Length_Years > 0;
    };

    const getLoanValidationKey = (loanId: number | null | undefined) => String(loanId ?? "new-loan");

    const setValues = (name: string, value: string | number, loan: ILoan) => {
      setLoans((prevState) => {
        const updatedLoans = prevState.map((l) => {
          if (l.LoanId === loan.LoanId) {
            const updatedLoan = { ...l, [name]: value || 0 };
            if (name === "Name") updatedLoan.isNameModified = true;

            const isValid = validateLoan(updatedLoan);
            setLoanValidation((prev) => ({
              ...prev,
              [getLoanValidationKey(loan.LoanId)]: isValid,
            }));

            return updatedLoan;
          }
          return l;
        });
        return updatedLoans;
      });
    };

    useEffect(() => {
      if (loans.length > 0 && activeIndex >= loans.length) {
        setActiveIndex(loans.length - 1);
      } else if (loans.length === 0) {
        setActiveIndex(0);
      }
    }, [loans.length, activeIndex]);

    useImperativeHandle(ref, () => ({
      validate: () => {
        const allLoansValid = loans.every((loan) => loanValidation[getLoanValidationKey(loan.LoanId)] !== false);

        const activeTabRef = tabRefs.current[activeIndex];
        const activeTabValid = activeTabRef?.validate() || false;

        setLoanValidation((prev) => ({
          ...prev,
          [getLoanValidationKey(loans[activeIndex]?.LoanId)]: activeTabValid,
        }));

        return allLoansValid && activeTabValid;
      },
    }));

    const tabs = useMemo(
      () =>
        loans.map((loan, index) => ({
          id: `loan-${loan.LoanId}`,
          header: loan.Name || `${formatDayCycle(index + 1)} loan`,
          content: (
            <LoanDetailsTab
              ref={(el) => (tabRefs.current[index] = el)}
              loan={loan}
              setValues={setValues}
              key={`loan-${loan.LoanId}`}
              isActive={index === activeIndex}
              loanAmount={index === 0 ? loanAmount : undefined}
            />
          ),
          closable: loans.length > 1,
        })),
      [loans, activeIndex, loanAmount, setValues]
    );

    const handleTabRemove = (e: any) => {
      const indexToRemove = e.index;

      const currentActiveIndex = activeIndex;
      const currentLoansLength = loans.length;

      setLoans((prevLoans) => {
        const newLoans = prevLoans.filter((_, i) => i !== indexToRemove);
        const renamedLoans = updateLoanNames(newLoans);
        return renamedLoans;
      });

      if (currentActiveIndex === indexToRemove) {
        if (indexToRemove === currentLoansLength - 1) {
          setActiveIndex(Math.max(0, indexToRemove - 1));
        } else {
          setActiveIndex(indexToRemove);
        }
      } else if (currentActiveIndex > indexToRemove) {
        setActiveIndex(currentActiveIndex - 1);
      }

      tabRefs.current = tabRefs.current.filter((_, i) => i !== indexToRemove);
    };

    const handleTabAdd = () => {
      if (loans.length >= 3) {
        return;
      }

      setLoans((prevLoans) => {
        const newLoan = applyUserDefaultLoanValues(createDefaultLoan(prevLoans.length));
        return [...prevLoans, newLoan];
      });

      setActiveIndex(loans.length);
    };

    return (
      <div className="w-full pt-2">
        <div className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2">
          {loans.length < 3 && (
            <div className="absolute top-2 right-2 z-10">
              <REIButton
                id="ldet-btn-addloan"
                label=""
                icon="pi pi-plus"
                tooltip={`Add Loan (${loans.length}/3)`}
                classNames=""
                onClick={handleTabAdd}
              />
            </div>
          )}
          {tabs.length > 0 && (
            <REITab
              id="ldet-tab-loans"
              handleTabClose={handleTabRemove}
              tabs={tabs}
              activeIndex={activeIndex}
              onTabChange={(e) => setActiveIndex(e.index)}
            />
          )}
        </div>
      </div>
    );
  }
);

export default LoanDetails;
